import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, CheckCircle, Clock, AlertTriangle, IndianRupee, Eye, Trash2, Download, Send, User, MoreVertical } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import ColumnFilter from '../../components/shared/ColumnFilter';
import ListPageHeader from '../../components/shared/ListPageHeader';
import StaffFilterSelect from '../../components/shared/StaffFilterSelect';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { getApiErrorMessage } from '../../utils/apiErrors';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const SalesInvoicesList = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({ total: 0, draft: 0, sent: 0, paid: 0, overdue: 0, total_value: 0, total_outstanding: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Role-based visibility
  const [isOwner, setIsOwner] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    invoice_number: '',
    customer_name: '',
    invoice_date: { min: '', max: '' },
    due_date: { min: '', max: '' },
    status: [],
    amount: { min: '', max: '' },
    balance: { min: '', max: '' },
    staff_member: ''
  });

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid) fetchInvoices();
  }, [bid, statusFilter, selectedStaffId]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const paramsObj = {};
      if (statusFilter !== 'all') paramsObj.status = statusFilter.toUpperCase();
      if (searchQuery) paramsObj.search = searchQuery;
      if (selectedStaffId) paramsObj.staff_id = selectedStaffId;

      const response = await api.get('/invoices', { params: paramsObj });
      const data = response.data;
      setInvoices(data.invoices || []);
      setStats(data.stats || {});
      setIsOwner(data.is_owner !== false);
      setStaffList(data.staff_list || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load invoices'));
    } finally {
      setLoading(false);
    }
  };

  // Apply all filters
  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = !searchQuery || 
      inv.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (inv.customer_name || inv.party_name_snapshot || '')?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesInvNo = !columnFilters.invoice_number || 
      inv.invoice_number?.toLowerCase().includes(columnFilters.invoice_number.toLowerCase());
    
    const matchesCustomer = !columnFilters.customer_name || 
      (inv.customer_name || inv.party_name_snapshot || '')?.toLowerCase().includes(columnFilters.customer_name.toLowerCase());
    
    const matchesInvDate = (!columnFilters.invoice_date?.min || new Date(inv.invoice_date) >= new Date(columnFilters.invoice_date.min)) &&
      (!columnFilters.invoice_date?.max || new Date(inv.invoice_date) <= new Date(columnFilters.invoice_date.max));
    
    const matchesDueDate = (!columnFilters.due_date?.min || new Date(inv.due_date) >= new Date(columnFilters.due_date.min)) &&
      (!columnFilters.due_date?.max || new Date(inv.due_date) <= new Date(columnFilters.due_date.max));
    
    const matchesStatus = columnFilters.status.length === 0 || columnFilters.status.includes(inv.status);
    
    const matchesAmount = (!columnFilters.amount?.min || (inv.grand_total || 0) >= parseFloat(columnFilters.amount.min)) &&
      (!columnFilters.amount?.max || (inv.grand_total || 0) <= parseFloat(columnFilters.amount.max));
    
    const matchesBalance = (!columnFilters.balance?.min || (inv.balance_due || 0) >= parseFloat(columnFilters.balance.min)) &&
      (!columnFilters.balance?.max || (inv.balance_due || 0) <= parseFloat(columnFilters.balance.max));
    
    // Staff member column filter (for owner view)
    const matchesStaffMember = !columnFilters.staff_member || 
      (inv.created_by_staff_name || '').toLowerCase().includes(columnFilters.staff_member.toLowerCase());
    
    return matchesSearch && matchesInvNo && matchesCustomer && matchesInvDate && matchesDueDate && matchesStatus && matchesAmount && matchesBalance && matchesStaffMember;
  });

  const handleAddInvoice = () => navigate(`/business/${bid}/sales/invoices/new`);
  const handleViewInvoice = (inv) => navigate(`/business/${bid}/sales/invoices/${inv.id}`);

  const handleSendInvoice = (invId, e) => {
    if (e) e.stopPropagation();
    setConfirmModal({
      title: 'Mark Invoice as Sent',
      description: 'This marks the invoice as sent to the customer. Status changes from Draft to Sent.',
      confirmLabel: 'Mark as Sent',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      iconBgClass: 'bg-blue-100',
      iconClass: 'text-blue-600',
      icon: Send,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          // api baseURL already includes /api -- do NOT prefix here (was
          // resulting in /api/api/invoices/.../send 404s on the list page
          // even though the detail page did it correctly).
          await api.patch(`/invoices/${invId}/send`);
          toast.success('Invoice marked as sent');
          fetchInvoices();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to send invoice'));
        }
      },
    });
  };

  const handleDeleteInvoice = (invId, e) => {
    if (e) e.stopPropagation();
    setConfirmModal({
      title: 'Delete Draft Invoice',
      description: 'This draft invoice will be permanently deleted. This cannot be undone.',
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      icon: Trash2,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.delete(`/invoices/${invId}`);
          toast.success('Invoice deleted');
          fetchInvoices();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to delete invoice'));
        }
      },
    });
  };

  const handleDownloadPdf = async (invId, e) => {
    if (e) e.stopPropagation();
    try {
      const response = await api.get(`/invoices/${invId}/pdf`, { responseType: 'blob' });
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${invId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error downloading PDF'));
    }
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    // Backend lifecycle: DRAFT -> (finalize) -> FINAL -> (send) -> SENT
    //                     DRAFT -> (send) -> SENT (skips FINAL)
    // FINAL means "finalized / issued but not yet marked sent to customer".
    // Previously the map was missing FINAL, so those rows fell through to
    // the DRAFT badge -- the UI said "Draft" for finalized invoices and
    // users expected Send/Delete actions that the backend (correctly)
    // blocks for FINAL.
    const badges = {
      DRAFT: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
      FINAL: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Final' },
      SENT: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Sent' },
      PARTIALLY_PAID: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Partial' },
      PAID: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Paid' },
      OVERDUE: { bg: 'bg-red-100', text: 'text-red-700', label: 'Overdue' },
      CANCELLED: { bg: 'bg-slate-100', text: 'text-slate-500', label: 'Cancelled' }
    };
    return badges[status] || badges.DRAFT;
  };

  return (
    <div className="h-full flex flex-col" data-testid="sales-invoices-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Sales Invoices"
        stats={[
          { icon: FileText, label: 'Total', value: stats.total, color: 'slate' },
          { icon: Clock, label: 'Draft', value: stats.draft, color: 'amber' },
          { icon: CheckCircle, label: 'Paid', value: stats.paid, color: 'emerald' },
          { icon: AlertTriangle, label: 'Overdue', value: stats.overdue, color: 'rose' },
          { icon: IndianRupee, label: 'Outstanding', value: formatCompactCurrency(stats.total_outstanding), color: 'blue' },
        ]}
        ctaLabel="New Invoice"
        onCtaClick={handleAddInvoice}
        extraControls={isOwner && staffList.length > 0 ? (
          <StaffFilterSelect
            staffList={staffList}
            selectedStaffId={selectedStaffId}
            onChange={setSelectedStaffId}
          />
        ) : null}
      />

      {/* TABLE AREA */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading invoices...</p>
              </div>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No invoices found</p>
              <p className="text-[13px] text-slate-400 mt-1 mb-4">Create your first invoice to get started</p>
              <button
                onClick={handleAddInvoice}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-[13px] font-medium"
              >
                Create Invoice
              </button>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
              {/* Single table: sticky thead eliminates two-table column misalignment */}
              <table className="tl-table w-full">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr>
                    <th className="tl-th tl-th-teal" style={{ width: '10%' }}>
                      <div className="flex items-center gap-1">
                        Invoice #
                        <ColumnFilter type="text" value={columnFilters.invoice_number} onChange={(val) => setColumnFilters({...columnFilters, invoice_number: val})} placeholder="Filter..." />
                      </div>
                    </th>
                    <th className="tl-th tl-th-teal" style={{ width: isOwner ? '14%' : '18%' }}>
                      <div className="flex items-center gap-1">
                        Customer
                        <ColumnFilter type="text" value={columnFilters.customer_name} onChange={(val) => setColumnFilters({...columnFilters, customer_name: val})} placeholder="Filter..." />
                      </div>
                    </th>
                    <th className="tl-th tl-th-teal" style={{ width: '9%' }}>
                      <div className="flex items-center gap-1">
                        Date
                        <ColumnFilter type="date" value={columnFilters.invoice_date} onChange={(val) => setColumnFilters({...columnFilters, invoice_date: val})} />
                      </div>
                    </th>
                    <th className="tl-th tl-th-teal" style={{ width: '9%' }}>
                      <div className="flex items-center gap-1">
                        Due Date
                        <ColumnFilter type="date" value={columnFilters.due_date} onChange={(val) => setColumnFilters({...columnFilters, due_date: val})} />
                      </div>
                    </th>
                    <th className="tl-th tl-th-teal" style={{ width: '8%' }}>
                      <div className="flex items-center gap-1">
                        Status
                        <ColumnFilter type="status" value={columnFilters.status} onChange={(val) => setColumnFilters({...columnFilters, status: val})} options={[
                          { value: 'DRAFT', label: 'Draft' },
                          { value: 'SENT', label: 'Sent' },
                          { value: 'PARTIALLY_PAID', label: 'Partial' },
                          { value: 'PAID', label: 'Paid' },
                          { value: 'OVERDUE', label: 'Overdue' }
                        ]} />
                      </div>
                    </th>
                    {/* Staff Member Column - Owner Only */}
                    {isOwner && (
                      <th className="tl-th tl-th-teal" style={{ width: '10%' }}>
                        <div className="flex items-center gap-1">
                          <User className="w-3.5 h-3.5" />
                          Staff
                          <ColumnFilter type="text" value={columnFilters.staff_member} onChange={(val) => setColumnFilters({...columnFilters, staff_member: val})} placeholder="Filter..." />
                        </div>
                      </th>
                    )}
                    <th className="tl-th tl-th-teal tl-th-right" style={{ width: '10%' }}>
                      <div className="flex items-center justify-end gap-1">
                        Amount
                        <ColumnFilter type="number" value={columnFilters.amount} onChange={(val) => setColumnFilters({...columnFilters, amount: val})} />
                      </div>
                    </th>
                    <th className="tl-th tl-th-teal tl-th-right" style={{ width: '9%' }}>
                      <div className="flex items-center justify-end gap-1">
                        Balance
                        <ColumnFilter type="number" value={columnFilters.balance} onChange={(val) => setColumnFilters({...columnFilters, balance: val})} />
                      </div>
                    </th>
                    <th className="tl-th tl-th-teal" style={{ width: '60px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((inv, index) => {
                        const statusBadge = getStatusBadge(inv.status);
                        return (
                        <tr
                          key={inv.id}
                          className={`tl-tr tl-tr-clickable hover:bg-teal-50/40 ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                          onClick={() => handleViewInvoice(inv)}
                        >
                          <td className="tl-td" style={{ width: '10%' }}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-[12px] font-medium text-teal-700">
                                  {inv.invoice_number?.slice(-2) || 'IN'}
                                </span>
                              </div>
                              <span className="tl-cell-primary">{inv.invoice_number}</span>
                            </div>
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: isOwner ? '14%' : '18%' }}>
                            {inv.customer_name || inv.party_name_snapshot || '—'}
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: '9%' }}>
                            {formatDate(inv.invoice_date)}
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: '9%' }}>
                            {formatDate(inv.due_date)}
                          </td>
                          <td className="tl-td" style={{ width: '8%' }}>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                              {statusBadge.label}
                            </span>
                          </td>
                          {/* Staff Member Column - Owner Only */}
                          {isOwner && (
                            <td className="tl-td" style={{ width: '10%' }}>
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                                  <User className="w-3 h-3 text-slate-500" />
                                </div>
                                <span className="text-[12px] text-slate-600 truncate">
                                  {inv.created_by_staff_name || 'Owner'}
                                </span>
                              </div>
                            </td>
                          )}
                          <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '10%' }}>
                            ₹{(inv.grand_total || 0).toLocaleString('en-IN')}
                          </td>
                          <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '9%' }}>
                            <span className={(inv.balance_due || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}>
                              ₹{(inv.balance_due || 0).toLocaleString('en-IN')}
                            </span>
                          </td>
                          <td className="tl-td" style={{ width: '60px' }} onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                                  title="Actions"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => handleViewInvoice(inv)}>
                                  <Eye className="w-4 h-4 mr-2 text-slate-500" />
                                  View
                                </DropdownMenuItem>
                                {(inv.status === 'DRAFT' || inv.status === 'FINAL') && (
                                  <DropdownMenuItem onClick={(e) => handleSendInvoice(inv.id, e)}>
                                    <Send className="w-4 h-4 mr-2 text-blue-600" />
                                    Mark as Sent
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={(e) => handleDownloadPdf(inv.id, e)}>
                                  <Download className="w-4 h-4 mr-2 text-slate-500" />
                                  Download PDF
                                </DropdownMenuItem>
                                {inv.status === 'DRAFT' && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={(e) => handleDeleteInvoice(inv.id, e)}
                                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                        );
                      })}
                </tbody>
              </table>
              </div>

              {/* TABLE FOOTER */}
                <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                  <p>Showing <span className="font-medium text-slate-700">{filteredInvoices.length}</span> invoices</p>
                  <p>Outstanding: <span className="font-medium text-slate-700">{formatCompactCurrency(stats.total_outstanding || 0)}</span></p>
                </div>
              </div>
            )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconClass={confirmModal?.iconClass}
        iconBgClass={confirmModal?.iconBgClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default SalesInvoicesList;
